import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Link } from "react-router-dom";
import MailIcon from "assets/icons/mail.svg";
import LockIcon from "assets/icons/lock.svg";
import {
  confirmResetPassword,
  resendSignUpCode,
  resetPassword,
} from "aws-amplify/auth";
import { Button } from "components/ui/button";

import { useQuery } from "@apollo/client";
import { CHECK_EMAIL_EXISTENCE } from "../../../apollo/queries/emailExistence";
import ModalStore from "components/Modal/ModalStore";
import { Modal, useModalStore } from "stores/modal/useModalStore";
import { useAuthStore } from "stores/auth/authStore";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "components/ui/input";
import { Loader2 } from "lucide-react";
import { PasswordInput } from "components/ui/password-input";

// Separate schemas for each stage
const emailSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" }),
});

const resetPasswordSchema = z
  .object({
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Invalid email address" }),
    code: z.string().min(1, { message: "Code is required" }),
    newPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long" }),
    repeatPassword: z
      .string()
      .min(8, { message: "Password must be at least 8 characters long" }),
  })
  .refine((data) => data.newPassword === data.repeatPassword, {
    message: "Passwords don't match",
    path: ["repeatPassword"],
  });

const ForgotPasswordForm = () => {
  const [forgetPasswordCodeSent, setForgetPasswordCodeSent] = useState(false);
  // Hook calls kept (bindings unused) to preserve hook order and store subscriptions.
  useForm({ mode: "onBlur" });
  const open = useModalStore((s) => s.open);
  const close = useModalStore((s) => s.close);
  useAuthStore(state => state.passwordVisibility);
  useAuthStore(action => action.toggleNewPassword);
  useAuthStore(action => action.toggleRepeatNewPassword);

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(120);
  const [showCountDown, setShowCountDown] = useState(true);

  const currentSchema = forgetPasswordCodeSent
    ? resetPasswordSchema
    : emailSchema;

  const form = useForm({
    mode: "onBlur",
    resolver: zodResolver(currentSchema),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const { refetch } = useQuery(CHECK_EMAIL_EXISTENCE, {
    variables: { email },
    skip: true,
  });

  useEffect(() => {
    let interval;
    if (forgetPasswordCodeSent) {
      interval = setInterval(() => {
        setCountdown((prevCountdown) =>
          prevCountdown > 0 ? prevCountdown - 1 : 0
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [forgetPasswordCodeSent]);

  useEffect(() => {
    if (countdown === 0) {
      setShowCountDown(false);
    }
  }, [countdown]);

  const handleResendCode = async () => {
    try {
      await resendSignUpCode({ username: email });
      setShowCountDown(true);
      setCountdown(120);
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    }
  };

  const checkEmailExistence = async (email) => {
    setLoading(true);
    try {
      const emailExists = await refetch({ email });
      if (!emailExists.data.isEmailExist) {
        ModalStore.openSimpleError({
          icon: undefined,
          title: "Error",
          subtitle: "Email does not exist",
          shouldCloseOnOverlayClick: false,
        });
        setLoading(false);
        return false;
      }
      return true;
    } catch (error) {
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
      setLoading(false);
      return false;
    }
  };

  const onHandleForgotPassword = async (data) => {
    setLoading(true);

    if (!forgetPasswordCodeSent) {
      const emailValid = await checkEmailExistence(data.email);
      if (emailValid) {
        setEmail(data.email);
        try {
          const response = await resetPassword({ username: data.email });
          if (
            response?.nextStep?.resetPasswordStep ===
            "CONFIRM_RESET_PASSWORD_WITH_CODE"
          ) {
            setForgetPasswordCodeSent(true);
            // Update the form with the email for the next stage
            form.setValue("email", data.email);
          } else if (response?.nextStep?.resetPasswordStep === "DONE") {
            ModalStore.openSimpleSuccess({
              title: "SUCCESS!",
              subtitle: "Successfully reset password.",
              shouldCloseOnOverlayClick: false,
            });
          }
        } catch (error) {
          ModalStore.openSimpleError({
            icon: undefined,
            title: "Error",
            subtitle: error?.message,
            shouldCloseOnOverlayClick: false,
          });
        }
      }
    } else {
      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: data.code,
          newPassword: data.newPassword,
        });
        ModalStore.openSimpleSuccess({
          title: "SUCCESS!",
          subtitle: "Password Reset Successfully",
          shouldCloseOnOverlayClick: false,
        });
        close();
      } catch (error) {
        ModalStore.openSimpleError({
          icon: undefined,
          title: "Error",
          subtitle: error?.message,
          shouldCloseOnOverlayClick: false,
        });
      }
    }


    setLoading(false);
  };

  const onHandleLoginClick = (event) => {
    event.preventDefault();
    close();
    open(Modal.Login);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const renderForm = () => (
    <div className="register-modal-content">
      <div className="register-content">
        <div className="register-content-form">
          <div>
            <Input
              name="email"
              label="Email"
              icon={
                <img src={MailIcon} alt="email" className="w-[15px] h-[15px]" />
              }
              placeholder="example@example.com"
              {...register("email")}
            />
            <div className="ml-2 h-2">
              {errors.email && (
                <p className="text-red-500 text-sm font-montserrat mt-1">
                  {errors.email?.message}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="register-content-submit mt-[19px]">
          <Button
            type="submit"
            size="xl"
            className="modal_form_btn"
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Send Code"
            )}
          </Button>
          <span className="register-content-submit__text">
            Have an account already?{" "}
            <Link
              onClick={onHandleLoginClick}
              className="font-bold underline text-primary"
              to="/"
            >
              Log in
            </Link>
          </span>
        </div>
        <div className="register-content-links">
          <Link
            to="/terms-and-conditions"
            className="text-secondary"
            onClick={() => close()}
          >
            Terms of service
          </Link>
          <Link
            to="/privacy-policy"
            className="text-secondary"
            onClick={() => close()}
          >
            Privacy policy
          </Link>
        </div>
      </div>
    </div>
  );

  const renderPasswordResetForm = () => (
    <div className="register-content">
      <div className="register-content-form">
        <div>
          <Input
            label="Code"
            placeholder="Enter code"
            {...register("code")}
            className="mt-4"
          />
          <div className="h-2">
            {errors.code && (
              <p className="text-red-500 text-sm font-montserrat mt-1">
                {errors.code?.message}
              </p>
            )}
          </div>
        </div>
        <div>
          <PasswordInput
            label="New Password"
            icon={
              <img
                src={LockIcon}
                alt="password"
                className="w-[15px] h-[15px]"
              />
            }
            placeholder="Enter new password"
            {...register("newPassword")}
            className="mt-4"
          />
          <div className="h-2">
            {errors.newPassword && (
              <p className="text-red-500 text-sm font-montserrat mt-1">
                {errors.newPassword?.message}
              </p>
            )}
          </div>
        </div>
        <div>
          <PasswordInput
            label={"Repeat password"}
            icon={
              <img
                src={LockIcon}
                alt="password"
                className="w-[15px] h-[15px]"
              />
            }
            {...register("repeatPassword")}
            placeholder="Repeat password"
          />
          <div className="h-2">
            {errors.repeatPassword && (
              <p className="text-red-500 text-sm font-montserrat mt-1">
                {errors.repeatPassword?.message}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="register-content-submit mt-[19px]">
        <Button
          type="button"
          className={`modal_form_btn ${countdown > 0 && "!bg-gray-300"}`}
          disabled={countdown > 0}
          onClick={handleResendCode}
          size="xl"
        >
          Resend code
        </Button>
        {showCountDown && (
          <span className="timer-text font-montserrat">
            {formatTime(countdown)}
          </span>
        )}
        <Button
          type="submit"
          className="modal_form_btn"
          size="xl"
          disabled={loading}
        >
          Confirm
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onHandleForgotPassword)}>
          {!forgetPasswordCodeSent ? renderForm() : renderPasswordResetForm()}
        </form>
      </FormProvider>
    </>
  );
};

export default ForgotPasswordForm;
